import React from 'react';
import Layout from '../../layout/Layout';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import axios from 'axios';
import { BlogArticlePageProps } from '../../types/pages/blog/BlogTypes';
import { CalendarIcon, PersonIcon } from '../../modules/Icons';
import moment from 'moment';
import { InternalAdsContainer } from '../../components/shared/InternalAd';
import BlogNavigation from '../../components/blog/BlogNavigation';
import errorRedirectHandler from '../../helpers/errorRedirectHandler';
import { seo, seoBlog } from '../../staticData/Seo';
import { getRootCategories, getPost } from '../../helpers/pages/blog/blogHelpers';
import BlogArticleVisitPlugin from '../../components/user-interest-analytics/BlogArticleVisitPlugin';

const BlogArticlePage: React.FC<BlogArticlePageProps> = (props) => {
  const { article, categories, recentArticles, preview } = props;
  const router = useRouter();

  const dynamicSeo = seoBlog.find((item) => item.path === router.asPath);

  const headContent = {
    title: article.title,
    keywords: ['actionvfx blog', 'vfx industry news', 'vfx blog'].concat(seo.keywords),
    description: dynamicSeo?.description || article.excerpt,
    og: {
      title: article.title,
      image: article.header_photo.url,
      description: dynamicSeo?.description || article.excerpt,
      type: 'article',
      url: `https://www.actionvfx.com/blog/${article.slug}`
    }
  };

  return (
    <Layout head={headContent}>
      <BlogArticleVisitPlugin article={article} />
      <BlogNavigation categories={categories} />
      {preview && (
        <div className="p-4 text-center text-black bg-yellow-500">
          This is a preview.
          <a href="/api/draft/disable" className="ml-2 underline">
            Exit preview mode
          </a>
        </div>
      )}
      <div className="bg-gray-900 post">
        <img
          className="z-0 block w-full mx-auto md:absolute"
          src={article.header_photo.url}
          alt={article.title}
        />
        <div className="relative z-10 md:flex justify-center md:pt-[300px] gap-12">
          <article className="bg-white w-full md:max-w-[942px] py-12 px-6 md:px-16 rounded-[5px]">
            <h1 className="text-gray-1000 font-bold inter text-[32px] md:text-[40px] leading-tight">
              {article.title}
            </h1>
            <div className="flex gap-8 text-gray-600 uppercase text-[12px] pt-4">
              <div className="">
                <CalendarIcon className="inline mr-1" />{' '}
                {moment(article?.shown_publish_date || article.publish_date).format('MMMM D, YYYY')}
              </div>

              {article.author && (
                <div className="">
                  <PersonIcon className="inline w-4 mr-1 fill-gray-500" />
                  {article.author.name}
                </div>
              )}
            </div>

            <div className="my-6">
              {article.categories.map((cat) => (
                <Link
                  key={cat.slug}
                  href={`/blog?category=${cat.slug}`}
                  className="bg-a-blue text-a-blue bg-opacity-10 rounded-[20px] py-2 px-3 text-[12px] mr-3"
                >
                  {cat.title}
                </Link>
              ))}
            </div>

            <div className="py-2 text-gray-1000 inter">
              {article.subtitle.length > 0 && (
                <h2
                  className="inter text-[24px] mb-6"
                  dangerouslySetInnerHTML={{ __html: article.subtitle }}
                ></h2>
              )}
              <div
                id="cms-content"
                className="content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>
            </div>
          </article>
          <div className="md:w-[460px]">
            <div className="blog-internal-ads">
              <InternalAdsContainer placement="blog_article" height="360" />
            </div>
            {recentArticles.length > 0 && (
              <div className="my-12 p-8 bg-gray-800 rounded-[5px] md:w-[460px] flex items-center justify-center shadow-lg">
                <div className="">
                  <h3 className="inter text-[21px]">Trending Articles</h3>
                  {recentArticles.map((a) => (
                    <a href={`/blog/${a.slug}`} key={a.slug}>
                      <article key={a.id} className="flex items-center gap-3 my-4">
                        <img
                          className="max-w-[120px]"
                          src={a.header_photo.thumb_url}
                          alt={a.title}
                        />
                        <h1 className="inter text-[15px]">{a.title}</h1>
                      </article>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="">
        <InternalAdsContainer placement="blog_main" height="360" />
      </div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ req, res, params, preview = false }) => {
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=59');

  const cmsApi: string = process.env.CMS_API_URL;
  const cmsApiKey: string = process.env.CMS_API_KEY;
  let article: any;

  try {
    article = await getPost(slug, preview);
  }
  catch (error) {
    return errorRedirectHandler(error);
  }

  if (!article) {
    return {
      notFound: true
    };
  }

  let rootCategoriesRes: any;
  try {
    rootCategoriesRes = await getRootCategories();
  }
  catch (error) {
    return errorRedirectHandler(error);
  }


  let articlesRes: any;
  try {
    articlesRes = await axios.get(
      `${cmsApi}/articles.json?order_by=publish_date&desc=true&limit=3`,
      {
        headers: {
          Authorization: `Bearer ${cmsApiKey}`
        }
      }
    );
  }
  catch (error) {
    return errorRedirectHandler(error);
  }

  return {
    props: {
      article: article,
      categories: rootCategoriesRes,
      recentArticles: articlesRes?.data?.data,
      preview: preview 
    }
  };
};

export default BlogArticlePage;
